import React, { useState } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  FAQ Data                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface FaqCategory {
  id: string;
  label: string;
  icon: string;
  items: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'orders',
    label: 'Orders & Payments',
    icon: 'zmdi-shopping-cart',
    items: [
      {
        id: 'o1',
        question: 'How do I place an order?',
        answer:
          'Browse our shop, add items to your cart, and proceed to checkout. You will need to provide your shipping address and payment details. Once confirmed, you will receive an order confirmation email with your order number.',
      },
      {
        id: 'o2',
        question: 'What payment methods do you accept?',
        answer:
          'We accept major credit and debit cards (Visa, Mastercard, American Express), PayPal, and bank transfer. All transactions are processed securely. Cash on delivery is also available in select regions.',
      },
      {
        id: 'o3',
        question: 'Can I modify or cancel my order after placing it?',
        answer:
          'Orders can be modified or cancelled within 1 hour of placement. After that, the order enters processing and changes may not be possible. Please contact our support team as quickly as possible if you need to make changes.',
      },
      {
        id: 'o4',
        question: 'Will I receive a confirmation email?',
        answer:
          'Yes. A confirmation email is sent to the address you provided at checkout immediately after your order is placed. It contains your order number, a summary of items, and your shipping details. Please check your spam folder if you do not see it.',
      },
      {
        id: 'o5',
        question: 'Is it safe to enter my payment details on your site?',
        answer:
          'Absolutely. Our checkout is secured with SSL encryption and we never store your card details on our servers. All payment processing is handled by PCI-DSS compliant payment providers.',
      },
    ],
  },
  {
    id: 'shipping',
    label: 'Shipping & Delivery',
    icon: 'zmdi-truck',
    items: [
      {
        id: 's1',
        question: 'How long does delivery take?',
        answer:
          'Delivery times vary by destination. Domestic orders typically arrive within 3–7 business days. International orders can take 7–21 business days depending on the destination country and customs processing.',
      },
      {
        id: 's2',
        question: 'How do I calculate my shipping cost?',
        answer:
          'Shipping costs are calculated at checkout based on your country and region. You can also use the shipping calculator on the cart page before proceeding to checkout. Visit our Shipping Rates page for a full breakdown by destination.',
      },
      {
        id: 's3',
        question: 'Do you offer free shipping?',
        answer:
          'Yes! Free shipping is available on orders that exceed the minimum threshold for your destination. The applicable threshold is shown at checkout and on our Shipping Rates page. Thresholds vary by country.',
      },
      {
        id: 's4',
        question: 'Can I track my order?',
        answer:
          'Yes. Once your order has shipped, you will receive an email with your tracking number. You can also visit our Track Order page and enter your order number and email address to check the latest status at any time.',
      },
      {
        id: 's5',
        question: 'Do you ship internationally?',
        answer:
          'Yes, we ship to many countries worldwide. You can view the full list of supported destinations along with their rates on our Shipping Rates page. If your country is not listed, please contact us and we will do our best to accommodate you.',
      },
      {
        id: 's6',
        question: 'What happens if my package is lost or delayed?',
        answer:
          'If your package has not arrived within the expected timeframe, please contact our support team with your order number. We will investigate with the carrier and arrange a replacement or refund if the package is confirmed lost.',
      },
    ],
  },
  {
    id: 'returns',
    label: 'Returns & Refunds',
    icon: 'zmdi-refresh-alt',
    items: [
      {
        id: 'r1',
        question: 'What is your return policy?',
        answer:
          'We accept returns within 30 days of delivery. Items must be unused, in their original packaging, and in resalable condition. Sale items and personalised products are not eligible for return unless they arrive damaged or defective.',
      },
      {
        id: 'r2',
        question: 'How do I initiate a return?',
        answer:
          'Contact our support team with your order number and reason for return. We will provide you with a return authorisation and instructions. Do not send items back without prior authorisation as they may not be accepted.',
      },
      {
        id: 'r3',
        question: 'How long does a refund take?',
        answer:
          'Once we receive and inspect your returned item, refunds are processed within 3–5 business days. The time for the funds to appear in your account depends on your payment provider — typically 5–10 business days.',
      },
      {
        id: 'r4',
        question: 'What if I received a damaged or incorrect item?',
        answer:
          'We sincerely apologise for that. Please contact us within 48 hours of delivery with photos of the damaged or incorrect item. We will arrange a replacement or full refund at no extra cost to you.',
      },
      {
        id: 'r5',
        question: 'Who pays for return shipping?',
        answer:
          'For returns due to our error (wrong or damaged items), we cover the return shipping cost. For change-of-mind returns, the customer is responsible for return shipping fees.',
      },
    ],
  },
  {
    id: 'products',
    label: 'Products & Stock',
    icon: 'zmdi-tag',
    items: [
      {
        id: 'p1',
        question: 'Are all products in stock?',
        answer:
          'Stock levels are updated in real time on our website. If a product shows as available, it is in stock. If an item is out of stock, you will see an "Out of Stock" indicator on the product page.',
      },
      {
        id: 'p2',
        question: 'Can I be notified when an out-of-stock item is available?',
        answer:
          'We are working on a restock notification feature. In the meantime, please contact our support team and we will manually notify you when the item becomes available again.',
      },
      {
        id: 'p3',
        question: 'Are product images accurate?',
        answer:
          'We strive to display product images as accurately as possible. However, colours may vary slightly depending on your screen settings and calibration. If you have any concerns about a product\'s appearance, feel free to reach out before ordering.',
      },
      {
        id: 'p4',
        question: 'Do you offer bulk or wholesale pricing?',
        answer:
          'Yes, we offer bulk pricing for qualifying orders. Please contact our sales team with the product(s) and quantities you are interested in, and we will provide a custom quote.',
      },
    ],
  },
  {
    id: 'account',
    label: 'Account & Support',
    icon: 'zmdi-account',
    items: [
      {
        id: 'a1',
        question: 'Do I need an account to place an order?',
        answer:
          'No, you can check out as a guest. However, creating an account allows you to view your order history, save your shipping details, and enjoy a faster checkout experience in the future.',
      },
      {
        id: 'a2',
        question: 'How do I contact customer support?',
        answer:
          'You can reach us via the contact form on our website or by emailing our support team directly. We aim to respond to all enquiries within 24 business hours.',
      },
      {
        id: 'a3',
        question: 'What are your customer support hours?',
        answer:
          'Our support team is available Monday to Friday, 9:00 AM – 6:00 PM (local time). We do our best to respond to weekend enquiries on the next business day.',
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Single accordion item                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

interface AccordionItemProps {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ item, isOpen, onToggle }) => (
  <div className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
    <button className="faq-item__q" onClick={onToggle} type="button">
      <span>{item.question}</span>
      <i className={`zmdi zmdi-chevron-down faq-chevron ${isOpen ? 'faq-chevron--up' : ''}`}></i>
    </button>
    {isOpen && (
      <div className="faq-item__a">
        <p>{item.answer}</p>
      </div>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Page                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const FAQs: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>(FAQ_CATEGORIES[0].id);
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const currentCategory = FAQ_CATEGORIES.find((c) => c.id === activeCategory)!;

  // When searching, show results across ALL categories
  const isSearching = searchQuery.trim().length > 0;
  const searchResults: (FaqItem & { categoryLabel: string })[] = isSearching
    ? FAQ_CATEGORIES.flatMap((cat) =>
        cat.items
          .filter(
            (item) =>
              item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
              item.answer.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((item) => ({ ...item, categoryLabel: cat.label }))
      )
    : [];

  const totalQuestions = FAQ_CATEGORIES.reduce((t, c) => t + c.items.length, 0);

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">FAQs</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Hero ── */}
          <div className="faq-hero">
            <div className="faq-hero__icon">
              <i className="zmdi zmdi-comment-list"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Frequently Asked Questions</h2>
            <p className="stext-113 cl6 faq-hero__sub">
              Find answers to the most common questions about our products, orders, shipping, and more.
            </p>
            <p className="stext-113 cl6" style={{ fontSize: 12, marginTop: 6 }}>
              {FAQ_CATEGORIES.length} categories &nbsp;·&nbsp; {totalQuestions} questions
            </p>

            {/* Search */}
            <div className="faq-search bor8 bg0">
              <i className="zmdi zmdi-search faq-search__icon"></i>
              <input
                className="stext-111 cl8"
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setOpenId(null);
                }}
              />
              {searchQuery && (
                <button className="faq-search__clear" onClick={() => setSearchQuery('')}>
                  <i className="zmdi zmdi-close"></i>
                </button>
              )}
            </div>
          </div>

          {/* ── Search results ── */}
          {isSearching ? (
            <div className="faq-search-results">
              {searchResults.length === 0 ? (
                <div className="faq-empty">
                  <i className="zmdi zmdi-search" style={{ fontSize: 44, color: '#ccc', marginBottom: 14 }}></i>
                  <p className="stext-113 cl6">No results for "<strong>{searchQuery}</strong>"</p>
                  <button className="faq-btn-ghost m-t-15" onClick={() => setSearchQuery('')}>
                    Clear search
                  </button>
                </div>
              ) : (
                <>
                  <p className="stext-111 cl6 p-b-20" style={{ fontSize: 13 }}>
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "<strong>{searchQuery}</strong>"
                  </p>
                  <div className="faq-accordion">
                    {searchResults.map((item) => (
                      <div key={item.id}>
                        <span className="faq-result-category">
                          {item.categoryLabel}
                        </span>
                        <AccordionItem
                          item={item}
                          isOpen={openId === item.id}
                          onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* ── Category tabs + accordion ── */
            <div className="faq-layout">

              {/* Sidebar tabs */}
              <nav className="faq-tabs">
                {FAQ_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    className={`faq-tab ${activeCategory === cat.id ? 'faq-tab--active' : ''}`}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setOpenId(null);
                    }}
                    type="button"
                  >
                    <i className={`zmdi ${cat.icon} faq-tab__icon`}></i>
                    <span className="faq-tab__label">{cat.label}</span>
                    <span className="faq-tab__count">{cat.items.length}</span>
                  </button>
                ))}
              </nav>

              {/* Accordion panel */}
              <div className="faq-panel">
                <h4 className="faq-panel__title">
                  <i className={`zmdi ${currentCategory.icon} m-r-10`} style={{ color: '#717fe0' }}></i>
                  {currentCategory.label}
                </h4>
                <div className="faq-accordion">
                  {currentCategory.items.map((item, i) => (
                    <div
                      key={item.id}
                      style={{ animationDelay: `${i * 50}ms` }}
                      className="faq-accordion__entry"
                    >
                      <AccordionItem
                        item={item}
                        isOpen={openId === item.id}
                        onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Still need help ── */}
          {!isSearching && (
            <div className="faq-cta">
              <div className="faq-cta__inner">
                <i className="zmdi zmdi-email" style={{ fontSize: 36, color: '#717fe0', marginBottom: 12, display: 'block' }}></i>
                <h5 className="mtext-110 cl2 p-b-10">Still have a question?</h5>
                <p className="stext-113 cl6 p-b-20">
                  Our support team is happy to help. Reach out and we'll get back to you within 24 hours.
                </p>
                <a
                  href="/contact"
                  className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
                  style={{ display: 'inline-flex' }}
                >
                  Contact Support
                </a>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Hero ── */
        .faq-hero {
          text-align: center;
          padding-bottom: 48px;
        }
        .faq-hero__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f4f4f9;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 30px;
          color: #717fe0;
        }
        .faq-hero__sub {
          max-width: 500px;
          margin: 0 auto 8px;
          line-height: 1.7;
        }

        /* ── Search ── */
        .faq-search {
          display: flex;
          align-items: center;
          max-width: 540px;
          margin: 24px auto 0;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 0 16px;
          background: #fff;
          box-shadow: 0 2px 10px rgba(0,0,0,0.04);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .faq-search:focus-within {
          border-color: #717fe0;
          box-shadow: 0 2px 14px rgba(113,127,224,0.12);
        }
        .faq-search__icon {
          color: #aaa;
          font-size: 18px;
          margin-right: 12px;
          flex-shrink: 0;
        }
        .faq-search input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          padding: 14px 0;
          font-size: 14px;
          color: #444;
          font-family: Poppins-Regular, sans-serif;
        }
        .faq-search__clear {
          background: none;
          border: none;
          cursor: pointer;
          color: #aaa;
          font-size: 16px;
          padding: 4px;
          line-height: 1;
          transition: color 0.2s;
        }
        .faq-search__clear:hover { color: #555; }

        /* ── Layout ── */
        .faq-layout {
          display: grid;
          grid-template-columns: 240px 1fr;
          gap: 32px;
          align-items: start;
        }

        /* ── Sidebar tabs ── */
        .faq-tabs {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: sticky;
          top: 20px;
        }
        .faq-tab {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border: 1px solid transparent;
          border-radius: 6px;
          background: none;
          cursor: pointer;
          text-align: left;
          font-family: Poppins-Regular, sans-serif;
          transition: all 0.2s;
          width: 100%;
        }
        .faq-tab:hover {
          background: #f5f5ff;
          border-color: #e0e0f8;
        }
        .faq-tab--active {
          background: #f0f2ff;
          border-color: #c8cef5;
        }
        .faq-tab__icon {
          font-size: 16px;
          color: #aaa;
          transition: color 0.2s;
          flex-shrink: 0;
        }
        .faq-tab--active .faq-tab__icon { color: #717fe0; }
        .faq-tab__label {
          flex: 1;
          font-size: 13px;
          color: #555;
          font-weight: 500;
          transition: color 0.2s;
        }
        .faq-tab--active .faq-tab__label { color: #333; font-weight: 600; }
        .faq-tab__count {
          font-size: 11px;
          background: #e8e8e8;
          color: #888;
          border-radius: 10px;
          padding: 1px 8px;
          transition: all 0.2s;
        }
        .faq-tab--active .faq-tab__count {
          background: #717fe0;
          color: #fff;
        }

        /* ── Panel ── */
        .faq-panel__title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f0f0f0;
          display: flex;
          align-items: center;
        }

        /* ── Accordion ── */
        .faq-accordion {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .faq-accordion__entry {
          animation: faqIn 0.3s ease both;
        }
        @keyframes faqIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .faq-item {
          border: 1px solid #e8e8e8;
          border-radius: 6px;
          overflow: hidden;
          background: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .faq-item:hover {
          border-color: #c8cef5;
        }
        .faq-item--open {
          border-color: #717fe0;
          box-shadow: 0 2px 12px rgba(113,127,224,0.08);
        }
        .faq-item__q {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 16px 20px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: Poppins-Regular, sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: #333;
          line-height: 1.5;
          transition: background 0.2s;
        }
        .faq-item--open .faq-item__q {
          background: #f8f9ff;
          color: #4a55c8;
        }
        .faq-chevron {
          font-size: 18px;
          color: #bbb;
          flex-shrink: 0;
          transition: transform 0.25s ease, color 0.2s;
        }
        .faq-chevron--up {
          transform: rotate(180deg);
          color: #717fe0;
        }
        .faq-item__a {
          padding: 0 20px 18px;
          animation: faqSlide 0.2s ease;
        }
        @keyframes faqSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .faq-item__a p {
          font-size: 13px;
          color: #666;
          line-height: 1.8;
          margin: 0;
        }

        /* ── Search results ── */
        .faq-search-results {
          max-width: 760px;
          margin: 0 auto;
        }
        .faq-result-category {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          color: #717fe0;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 6px;
          margin-top: 16px;
        }

        /* ── Empty state ── */
        .faq-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 60px 20px;
          text-align: center;
        }
        .faq-btn-ghost {
          background: none;
          border: 1px solid #d0d0d0;
          border-radius: 4px;
          padding: 8px 20px;
          font-size: 12px;
          color: #666;
          cursor: pointer;
          font-family: Poppins-Regular, sans-serif;
          transition: all 0.2s;
        }
        .faq-btn-ghost:hover {
          border-color: #717fe0;
          color: #717fe0;
        }

        /* ── CTA ── */
        .faq-cta {
          margin-top: 60px;
        }
        .faq-cta__inner {
          background: #f8f9ff;
          border: 1px solid #dde2f8;
          border-radius: 8px;
          padding: 40px 30px;
          text-align: center;
          max-width: 480px;
          margin: 0 auto;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .faq-layout {
            grid-template-columns: 1fr;
          }
          .faq-tabs {
            position: static;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 6px;
          }
          .faq-tab {
            flex: 0 0 auto;
            padding: 8px 14px;
          }
          .faq-tab__label {
            font-size: 12px;
          }
        }
        @media (max-width: 480px) {
          .faq-item__q {
            font-size: 13px;
            padding: 14px 16px;
          }
          .faq-item__a {
            padding: 0 16px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default FAQs;